import { toast } from "react-hot-toast";
import {
  Form,
  ActionFunctionArgs,
  useLoaderData,
  useLocation,
  redirect,
} from "react-router-dom";
import { useState, useEffect } from "react";
import { nanoid } from "nanoid";
import { Input, TextArea } from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {
  GET_LIST_ATTENDEES_BY_ORGANIZATION,
  GET_LIST_EVENTS_BY_ORGANIZATION,
} from "../../queries";
import { organizationStore } from "../../store/organization";
import { Attendee } from "../../types/attendee";
import { Event } from "../../types/event";
import { CREATE_RFID } from "./mutations";
import PriceInput from "../../components/inputs/PriceInput";
import { userStore } from "../../store/user";
import { PrimaryButton } from "../../components/Button";

const client = createApolloClient();

const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

interface TokenBalance {
  balance: number;
  redeemable_token_id: string;
  redeemable_token_name: string;
}

type TokenBalances = {
  [key: string]: TokenBalance;
};

type Token = {
  name: string;
  balance: number;
};

export const rfidAssetCreateLoader = async () => {
  const organizationId = getState().organizationId;
  const user = getUserStore().user;

  let where: any = {
    organization_id: { _eq: organizationId },
  };
  if (user?.events) {
    where = {
      ...where,
      id: {
        _in: user?.events,
      },
    };
  }

  const [{ data: attendeesData }, { data: eventsData }] = await Promise.all([
    client.query({
      query: GET_LIST_ATTENDEES_BY_ORGANIZATION,
      variables: {
        organizationId,
      },
    }),
    client.query({
      query: GET_LIST_EVENTS_BY_ORGANIZATION,
      variables: {
        where,
      },
    }),
  ]);

  return {
    attendees: attendeesData.attendees,
    events: eventsData.events,
  };
};

export const rfidAssetCreateAction = async ({
  request,
}: ActionFunctionArgs) => {
  const body = await request.formData();
  const user = getUserStore().user;

  const cashBalanceString = body.get("cash_balance") as string;
  const cashBalance = parseFloat(
    cashBalanceString.length ? cashBalanceString : "0"
  );
  const promoBalanceString = body.get("promo_balance") as string;
  const promoBalance = parseFloat(
    promoBalanceString.length ? promoBalanceString : "0"
  );

  const attendeeId = body.get("attendee_id");
  const attendeePin = body.get("attendee_pin") as string; // Hidden field when coming from attendee

  // Parse tokens from form data
  const tokensString = body.get("tokens_balance") as string;
  let tokensBalance: TokenBalances = {};
  try {
    tokensBalance = tokensString ? JSON.parse(tokensString) : {};
  } catch (e) {
    console.error("Failed to parse tokens_balance", e);
  }

  // Use attendee's PIN if provided, otherwise use the PIN from form or null
  const pinValue = attendeePin || body.get("last_four_phone_numbers") || null;

  const input = {
    uid: body.get("uid"),
    event_id: parseInt(body.get("event_id") as string),
    attendee_id: attendeeId ? parseInt(attendeeId as string) : null,
    access_type: body.get("access_type"),
    reg_id: body.get("reg_id") || "",
    is_active: body.get("is_active") === "true",
    cash_balance: Math.round(cashBalance * 100),
    promo_balance: Math.round(promoBalance * 100),
    tokens_balance: tokensBalance,
    notes: body.get("notes") || "",
    last_four_phone_numbers: pinValue,
    personnal_pin: pinValue,
    last_updated_by: user?.id,
  };

  try {
    const { data } = await client.mutate({
      mutation: CREATE_RFID,
      variables: {
        input,
      },
    });

    toast.success("RFID Asset created successfully");
    return redirect(`/rfid-assets/list`);
  } catch (error: any) {
    const errorType = error?.graphQLErrors?.[0]?.extensions?.code;
    const message = error?.graphQLErrors?.[0]?.message;
    if (errorType === "constraint-violation") {
      toast.error(message);
    } else {
      toast.error("Failed to create RFID Asset: " + error);
    }
    return redirect("/rfid-assets/create");
  }
};

export const RfidAssetCreate = () => {
  const { attendees, events } = useLoaderData() as {
    attendees: Attendee[];
    events: Event[];
  };
  const location = useLocation();
  const attendeeFromState = location.state?.attendee;

  const [generatedUid, setGeneratedUid] = useState<string>("");
  const [pin, setPin] = useState<string>("");
  const [selectedAttendeeId, setSelectedAttendeeId] = useState<string | number>(attendeeFromState?.id || "");
  const [selectedAttendeePin, setSelectedAttendeePin] = useState<string>(attendeeFromState?.personnal_pin || "");
  const [cashBalance, setCashBalance] = useState<number>(0);
  const [promoBalance, setPromoBalance] = useState<number>(0);
  const [tokens, setTokens] = useState<Token[]>([{ name: "", balance: 0 }]);

  useEffect(() => {
    setGeneratedUid(nanoid(12));
    // Debug: Log attendee data when component mounts
    if (attendeeFromState) {
      console.log("Attendee from state:", attendeeFromState);
      console.log("Attendee PIN:", attendeeFromState.personnal_pin);
    }
  }, []);

  const handleCashBalanceChange = (value: number) => {
    setCashBalance(value);
  };

  const handlePromoBalanceChange = (value: number) => {
    setPromoBalance(value);
  };

  const handlePinChange = (value: string | number | undefined) => {
    const digitsOnly = String(value || '').replace(/\D/g, '').slice(0, 4);
    setPin(digitsOnly);
  };

  const handleAddToken = () => {
    setTokens([...tokens, { name: "", balance: 0 }]);
  };

  const handleRemoveToken = (index: number) => {
    if (tokens.length > 1) {
      setTokens(tokens.filter((_, i) => i !== index));
    }
  };

  const handleTokenChange = (
    index: number,
    field: keyof Token,
    value: string | number | undefined
  ) => {
    const updatedTokens = [...tokens];
    if (field === "balance") {
      updatedTokens[index].balance = Number(value) || 0;
    } else {
      updatedTokens[index].name = String(value);
    }
    setTokens(updatedTokens);
  };

  // Convert tokens array back to the proper object format for submission
  const getTokenBalancesObject = () => {
    const tokenBalances: TokenBalances = {};
    tokens.forEach((token) => {
      if (token.name.trim() !== "") {
        tokenBalances[token.name] = {
          balance: token.balance,
          redeemable_token_id: token.name,
          redeemable_token_name: token.name,
        };
      }
    });
    return tokenBalances;
  };

  return (
    <Form action={`/rfid-assets/create`} method="post">
      <FormLayout>
        {attendeeFromState && (
          <div className="col-span-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-blue-900">
                <strong>Creating cashless asset for:</strong> {attendeeFromState.first_name} {attendeeFromState.last_name} ({attendeeFromState.email})
              </p>
            </div>
          </div>
        )}
        <div className="col-span-6">
          <Input label="UID" name="uid" value={generatedUid} required readOnly />
        </div>
        <input type="hidden" name="is_active" value={selectedAttendeeId ? "true" : "false"} />
        <div className="col-span-6">
          <CustomSelect
            title="Event"
            name="event_id"
            required
            defaultValue={attendeeFromState?.event_id}
            options={[
              ...events.map((event) => ({
                label: event.name,
                value: event.id,
              })),
            ]}
          />
        </div>
        <div className="col-span-6">
          <CustomSelect
            title="Attendee (Optional)"
            name="attendee_id"
            defaultValue={attendeeFromState?.id || ""}
            onChange={(value) => {
              setSelectedAttendeeId(value);
              const selectedAttendee = attendees.find(a => a.id === value);
              setSelectedAttendeePin(selectedAttendee?.personnal_pin || "");
            }}
            options={[
              { label: "None", value: "" },
              ...attendees.map((attendee) => ({
                label: `${attendee.first_name} ${attendee.last_name} (${attendee.email}) (${attendee.phone_number || 'No Phone'})`,
                value: attendee.id,
              })),
            ]}
          />
        </div>
        {!selectedAttendeeId && (
          <div className="col-span-6">
            <Input
              label="PIN (4 digits)"
              name="last_four_phone_numbers"
              type="text"
              value={pin}
              onChange={handlePinChange}
              maxLength={4}
              placeholder="0000"
            />
          </div>
        )}
        {selectedAttendeeId && (
          <input type="hidden" name="attendee_pin" value={selectedAttendeePin} />
        )}
        <div className="col-span-6">
          <Input label="Print ID" name="reg_id" />
        </div>
        <div className="col-span-6">
          <Input
            label="Access Type"
            name="access_type"
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <PriceInput
            label="Cash Balance"
            name="cash_balance"
            value={cashBalance}
            onChange={(value) => handleCashBalanceChange(value ? value : 0)}
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <PriceInput
            label="Promo Balance"
            name="promo_balance"
            value={promoBalance}
            onChange={(value) => handlePromoBalanceChange(value ? value : 0)}
          />
        </div>
        <div className="col-span-6">
          <p className="block text-sm font-medium text-gray-700 mb-3">
            Token Balances:
          </p>
          <div className="space-y-3">
            {tokens.map((token, index) => (
              <div key={index} className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <Input
                    label="Token Name"
                    name={`tokens[${index}][name]`}
                    value={token.name}
                    onChange={(value) => handleTokenChange(index, "name", String(value))}
                  />
                </div>
                <div className="col-span-4">
                  <Input
                    label="Balance"
                    name={`tokens[${index}][balance]`}
                    value={token.balance}
                    type="number"
                    onChange={(value) => handleTokenChange(index, "balance", Number(value) || 0)}
                  />
                </div>
                <div className="col-span-2 flex items-end">
                  {tokens.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveToken(index)}
                      className="mb-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div className="mt-2">
              <PrimaryButton type="button" onClick={handleAddToken}>
                + Add Token
              </PrimaryButton>
            </div>
          </div>
        </div>
        <div className="col-span-6">
          <TextArea label="Notes" name="notes" />
        </div>
        <input
          type="hidden"
          name="tokens_balance"
          value={JSON.stringify(getTokenBalancesObject())}
        />
      </FormLayout>
    </Form>
  );
};
